import { useCallback, useMemo } from 'react';

import { useTableContext } from 'lib/context/TableContext/TableContext';

import Button from 'components/shared/Button/Button';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

const OrderOptions = () => {
  const { colDefs, setColDefs, gridApi } = useTableContext();

  const currentSort = useMemo(() => {
    const state = gridApi?.getColumnState?.() || [];
    const active = state.find((c) => c.sort === 'asc' || c.sort === 'desc');
    if (active) return { field: active.colId, dir: active.sort };
    const fromDefs = colDefs?.find((c) => c.sort === 'asc' || c.sort === 'desc');
    return fromDefs ? { field: fromDefs.field, dir: fromDefs.sort } : null;
  }, [gridApi, colDefs]);

  const clearOrdering = useCallback(() => {
    if (gridApi?.applyColumnState) {
      gridApi.applyColumnState({
        defaultState: { sort: null, sortIndex: null },
        state: []
      });
    }
    setColDefs((prev) => prev.map((c) => ({ ...c, sort: null, sortIndex: null })));
  }, [gridApi, setColDefs]);

  const orderByField = useCallback(
    (field, dir = 'asc') => {
      if (!field) return clearOrdering();
      if (gridApi?.applyColumnState) {
        gridApi.applyColumnState({
          defaultState: { sort: null, sortIndex: null },
          state: [{ colId: field, sort: dir, sortIndex: 0 }]
        });
      }
      setColDefs((prev) =>
        prev.map((c) => ({
          ...c,
          sort: c.field === field ? dir : null,
          sortIndex: c.field === field ? 0 : null
        }))
      );
    },
    [gridApi, setColDefs, clearOrdering]
  );

  const toggleDirection = useCallback(() => {
    if (!currentSort?.field) return;
    const next = currentSort.dir === 'asc' ? 'desc' : 'asc';
    orderByField(currentSort.field, next);
  }, [currentSort, orderByField]);

  const options = useMemo(() => {
    const columnOptions = colDefs.map((col) => ({
      label: col.headerName,
      value: col.field,
      onClick: () => orderByField(col.field, 'asc')
    }));
    return [{ label: 'Default', value: null, onClick: () => clearOrdering() }, ...columnOptions];
  }, [colDefs, orderByField, clearOrdering]);

  const currentOrder = useMemo(() => {
    return colDefs.find((c) => c.field === currentSort?.field);
  }, [colDefs, currentSort]);

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <Typography icon="arrow-swap-up-mono" iconColor="neutral-700" variant="label" size="medium">
        Order
      </Typography>
      <div className="flex items-center gap-1">
        <Popover
          position="left"
          panelClassName="p-0 border border-neutral-100 shadow-spread-medium rounded border-solid gap-y-1"
          optionGroupLabelClassName="h-10 px-4 py-2"
          options={options}
          customButtonTrigger={({ open }) => (
            <Button
              size="xs"
              text={currentSort ? currentOrder?.headerName : 'Default'}
              variant="tertiary"
              iconColor="neutral"
              iconRight={open ? 'chevron-up-linear' : 'chevron-down-linear'}
              iconShade={700}
              className={open && 'whitespace-nowrap bg-neutral-100'}
            />
          )}
        />
        {currentOrder && (
          <Button
            size="xs"
            icon={
              currentOrder?.sort === 'desc'
                ? 'order-newest-first-linear'
                : 'order-oldest-first-linear'
            }
            variant="tertiary"
            color="neutral"
            iconShade={700}
            className="h-6 w-6 p-0"
            iconSize={16}
            onClick={toggleDirection}
            isDisabled={!currentOrder}
          />
        )}
      </div>
    </div>
  );
};

export default OrderOptions;
